/* Myo-Control Industrial Data Room — frontend.
   Palette : #172343 (bleu) / #3C66AE (bleu sec) / #ED761E (orange accent).
*/
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  LayoutDashboard, Users, FileQuestion, Shield, LogOut, Mail, KeyRound,
  Loader2, Upload, Plus, ChevronDown, X, Database, Activity, Eye, ArrowLeft, Menu,
  UserPlus, Trash2, RefreshCw, Copy, Check, AlertCircle,
  Sparkles, TrendingDown, Map, Award, Lock,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine,
} from "recharts";

const apiBase = (() => {
  if (typeof window !== "undefined") {
    return `${window.location.protocol}//${window.location.host}/api/myo`;
  }
  return "/api/myo";
})();

const TOKEN_KEY = "myo_token";
const USER_KEY = "myo_user";

/**
 * Robust error-message extractor that handles every shape FastAPI/Pydantic
 * can return: string detail, array of Pydantic validation errors, network
 * timeout, no response, etc. Falls back to a user-friendly default.
 */
function extractErrorMessage(e, fallback = "Erreur") {
  // Network / no-response error
  if (e?.message === "Network Error" || !e?.response) {
    return e?.message ? `Réseau : ${e.message}` : fallback;
  }
  const detail = e?.response?.data?.detail;
  if (!detail) {
    // No detail field — fall back to status text
    const status = e?.response?.status;
    return status ? `Erreur HTTP ${status}` : fallback;
  }
  if (typeof detail === "string") return detail;
  // Pydantic validation array: [{type, loc, msg, ...}, ...]
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc.slice(-1)[0] : "champ";
        return `${field}: ${d?.msg || "invalide"}`;
      })
      .filter(Boolean)
      .join(" · ") || fallback;
  }
  // Object detail (rare)
  if (typeof detail === "object") {
    return detail?.msg || detail?.message || JSON.stringify(detail);
  }
  return String(detail) || fallback;
}

/* ---------------- Branding ---------------- */
const Brand = ({ size = 32, light = false }) => (
  <div className="flex items-center gap-2.5">
    <img
      src="/logos/myo-control.jpeg"
      alt="Myo-Control"
      style={{ width: size, height: size }}
      className="rounded-md object-contain bg-white p-0.5 ring-1 ring-white/20"
      data-testid="myo-brand-logo"
    />
    <div>
      <div className={`font-semibold leading-tight ${light ? "text-white text-[15px]" : "text-[#172343] text-lg"}`}>Myo-Control</div>
      <div className={`uppercase tracking-[0.2em] ${light ? "text-white/60 text-[10px]" : "text-[#3C66AE] text-[11px]"}`}>Industrial Data Room</div>
    </div>
  </div>
);

/* ---------------- Auth hook ---------------- */
function useAuth() {
  const [token, setToken] = useState(() => {
    const tk = localStorage.getItem(TOKEN_KEY) || "";
    if (tk) axios.defaults.headers.common["Authorization"] = `Bearer ${tk}`;
    return tk;
  });
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
  });
  const login = useCallback((tk, u) => {
    localStorage.setItem(TOKEN_KEY, tk); localStorage.setItem(USER_KEY, JSON.stringify(u));
    // Set axios header SYNCHRONOUSLY so next API call carries the token
    axios.defaults.headers.common["Authorization"] = `Bearer ${tk}`;
    setToken(tk); setUser(u);
  }, []);
  const logout = useCallback(async () => {
    try { await axios.post(`${apiBase}/auth/logout`); } catch { /* ignore */ }
    delete axios.defaults.headers.common["Authorization"];
    localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY);
    setToken(""); setUser(null);
  }, []);
  useEffect(() => {
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    else delete axios.defaults.headers.common["Authorization"];
  }, [token]);

  // Defensive request interceptor: ALWAYS overwrite the Authorization header
  // for /api/myo/* calls with the Myo token, regardless of what other axios
  // interceptors (admin/pro AuthContext) may have set.
  useEffect(() => {
    const id = axios.interceptors.request.use((cfg) => {
      if (cfg.url && cfg.url.includes("/api/myo/")) {
        const tk = localStorage.getItem(TOKEN_KEY);
        if (tk) {
          cfg.headers = { ...cfg.headers, Authorization: `Bearer ${tk}` };
        } else if (cfg.headers && cfg.headers.Authorization) {
          // No Myo token → strip any Authorization header that another
          // interceptor may have attached (avoids leaking admin/pro tokens to /myo).
          delete cfg.headers.Authorization;
        }
      }
      return cfg;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  // Auto-logout on 401 from any myo endpoint
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        if (status === 401 && url.includes("/api/myo/") && !url.includes("/auth/login") && !url.includes("/auth/verify-code")) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setToken(""); setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);
  return { token, user, login, logout };
}

/* ---------------- Login (2-step) ---------------- */
function Login({ onLogin }) {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [emailMasked, setEmailMasked] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit1 = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${apiBase}/auth/login`, { identifier, password });
      // skip_otp users: server returns token + user directly
      if (data.token && data.user) { onLogin(data.token, data.user); return; }
      setEmailMasked(data.email_masked); setStep(2);
    } catch (e) { setErr(e?.response?.data?.detail || "Connexion impossible"); }
    finally { setBusy(false); }
  };
  const submit2 = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${apiBase}/auth/verify-code`, { identifier, code });
      onLogin(data.token, data.user);
    } catch (e) { setErr(e?.response?.data?.detail || "Code invalide"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f5f7fb] to-[#e8eef7] p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl border border-[#e5e9f1] p-8" data-testid="myo-login">
        <div className="mb-8 flex justify-center">
          <img src="/logos/myo-control.jpeg" alt="Myo-Control"
            className="h-20 w-auto object-contain"
            data-testid="myo-brand-logo-large" />
        </div>
        {step === 1 ? (
          <form onSubmit={submit1} className="grid gap-4" data-testid="myo-login-step1">
            <div>
              <label className="block text-[12px] font-medium text-[#172343] mb-1.5">Identifiant ou email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3C66AE]" />
                <input data-testid="myo-login-identifier" type="text" required value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)} autoComplete="username"
                  className="w-full pl-10 pr-3 py-2.5 border border-[#cbd3e1] rounded-lg text-[14px] text-[#172343] placeholder:text-[#94a3b8] bg-white focus:outline-none focus:border-[#3C66AE] focus:ring-2 focus:ring-[#3C66AE]/20" />
              </div>
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#172343] mb-1.5">Mot de passe</label>
              <div className="relative">
                <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3C66AE]" />
                <input data-testid="myo-login-pwd" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                  className="w-full pl-10 pr-3 py-2.5 border border-[#cbd3e1] rounded-lg text-[14px] text-[#172343] placeholder:text-[#94a3b8] bg-white focus:outline-none focus:border-[#3C66AE] focus:ring-2 focus:ring-[#3C66AE]/20" />
              </div>
            </div>
            {err && <div className="text-[13px] text-red-600 bg-red-50 p-2 rounded-lg" data-testid="myo-login-err">{err}</div>}
            <button data-testid="myo-login-submit" type="submit" disabled={busy}
              className="w-full bg-[#172343] hover:bg-[#0F1A33] text-white py-2.5 rounded-lg text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 size={14} className="animate-spin" />}Se connecter
            </button>
          </form>
        ) : (
          <form onSubmit={submit2} className="grid gap-4" data-testid="myo-login-step2">
            <p className="text-[13px] text-[#3C66AE] leading-relaxed">
              Un code à 6 chiffres a été envoyé à <strong>{emailMasked}</strong>. Saisissez-le ci-dessous.
            </p>
            <input data-testid="myo-login-code" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
              required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              autoFocus
              className="w-full px-4 py-3 border border-[#e5e9f1] rounded-lg text-center text-[24px] font-mono tracking-[8px] focus:outline-none focus:border-[#ED761E]" />
            {err && <div className="text-[13px] text-red-600 bg-red-50 p-2 rounded-lg">{err}</div>}
            <button data-testid="myo-login-verify" type="submit" disabled={busy || code.length !== 6}
              className="w-full bg-[#ED761E] hover:bg-[#D86813] text-white py-2.5 rounded-lg text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 size={14} className="animate-spin" />}Valider le code
            </button>
            <button data-testid="myo-login-back" type="button"
              onClick={() => { setStep(1); setCode(""); setErr(""); }}
              className="text-[12px] text-[#3C66AE] hover:underline">Recommencer</button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------------- Layout ---------------- */
function Layout({ user, onLogout, page, setPage, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const items = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "industrial", "observer"] },
    { key: "patients", label: "Patients", icon: Users, roles: ["super_admin", "industrial", "observer"] },
    { key: "governance", label: "Gouvernance", icon: FileQuestion, roles: ["super_admin", "industrial", "observer"] },
    { key: "mssante", label: "Boîtes MSSanté", icon: Mail, roles: ["super_admin", "industrial", "observer"] },
    { key: "team", label: "Collaborateurs", icon: UserPlus, roles: ["super_admin", "industrial"] },
    { key: "admin", label: "Administration", icon: Shield, roles: ["super_admin"] },
  ];
  const navigate = (key) => { setPage(key); setSidebarOpen(false); };
  return (
    <div className="min-h-screen bg-[#f5f7fb] flex" data-testid="myo-app">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-[#172343] text-white flex items-center justify-between px-4 border-b border-white/10">
        <Brand size={28} light />
        <button onClick={() => setSidebarOpen(true)} data-testid="myo-menu-open"
          className="p-2 rounded-md hover:bg-white/10" aria-label="Ouvrir le menu">
          <Menu size={20} />
        </button>
      </header>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar (drawer on mobile, fixed on desktop) */}
      <aside data-testid="myo-sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#172343] text-white flex flex-col transform transition-transform duration-200 ease-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}>
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <Brand size={36} light />
          <button onClick={() => setSidebarOpen(false)} data-testid="myo-menu-close"
            className="lg:hidden p-1.5 rounded-md hover:bg-white/10" aria-label="Fermer le menu">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {items.filter((it) => it.roles.includes(user.role)).map((it) => (
            <button key={it.key} onClick={() => navigate(it.key)}
              data-testid={`myo-nav-${it.key}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] mb-0.5 transition ${
                page === it.key ? "bg-[#ED761E] text-white" : "text-white/80 hover:bg-white/5 hover:text-white"
              }`}>
              <it.icon size={16} />{it.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-2 mb-2">
            <div className="text-[12px] font-medium truncate">{user.name}</div>
            <div className="text-[10px] text-white/60 uppercase tracking-wider">
              {user.role === "super_admin" ? "Admin MetaConnect" : user.role === "industrial" ? "Industriel" : "Observateur"}
            </div>
          </div>
          <button onClick={onLogout} data-testid="myo-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] text-white/80 hover:bg-red-500/20 hover:text-red-300">
            <LogOut size={14} />Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 pt-[72px] lg:pt-8 text-[#172343] min-w-0">{children}</main>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const { data } = await axios.get(`${apiBase}/dashboard/stats`); if (alive) setStats(data); }
      catch (e) { if (alive) setErr(e?.response?.data?.detail || "Erreur"); }
    })();
    return () => { alive = false; };
  }, []);
  if (err) return <div className="text-red-600">{err}</div>;
  if (!stats) return <div className="text-[#3C66AE]"><Loader2 size={18} className="animate-spin inline mr-2" />Chargement…</div>;

  const KPI = ({ label, value, accent, sub }) => (
    <div className="bg-white rounded-xl border border-[#e5e9f1] p-4 sm:p-5" data-testid={`myo-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-[#3C66AE]">{label}</div>
      <div className={`mt-2 text-2xl sm:text-3xl font-semibold ${accent ? "text-[#ED761E]" : "text-[#172343]"}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#3C66AE]/70 mt-1">{sub}</div>}
    </div>
  );

  const fmtDuration = (seconds) => {
    const s = Number(seconds) || 0;
    const m = Math.floor(s / 60);
    const r = s % 60;
    if (m === 0) return `${r}s`;
    return `${m} min ${String(r).padStart(2, "0")}`;
  };

  /* Tiny inline SVG sparkline — zero dependency, draws a 4-week trend line.
     For session duration, a DESCENDING line is positive (users got faster). */
  const Sparkline = ({ data, positiveTrend = "down", width = 80, height = 22 }) => {
    if (!Array.isArray(data) || data.length < 2) return null;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = width / (data.length - 1);
    const points = data.map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const path = `M ${points.join(" L ")}`;
    const lastX = (data.length - 1) * stepX;
    const lastY = height - ((data[data.length - 1] - min) / range) * (height - 4) - 2;

    // Determine if trend is "good" (descending for session time, ascending otherwise)
    const trendDelta = data[data.length - 1] - data[0];
    const isPositive = positiveTrend === "down" ? trendDelta < 0 : trendDelta > 0;
    const stroke = isPositive ? "#1FB585" : "#ED761E";

    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
           className="overflow-visible" aria-hidden="true">
        <path d={path} fill="none" stroke={stroke} strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={lastX} cy={lastY} r="2.5" fill={stroke} />
      </svg>
    );
  };

  const sessionHistory = stats.avg_session_history;
  const sessionDelta = (Array.isArray(sessionHistory) && sessionHistory.length >= 2)
    ? sessionHistory[sessionHistory.length - 1] - sessionHistory[0]
    : 0;
  const sessionDeltaPct = (Array.isArray(sessionHistory) && sessionHistory[0])
    ? Math.round((sessionDelta / sessionHistory[0]) * 100)
    : 0;

  return (
    <div data-testid="myo-dashboard">
      <h1 className="text-[20px] sm:text-2xl font-semibold text-[#172343] mb-1">Vue d'ensemble</h1>
      <p className="text-[13px] text-[#3C66AE] mb-5 sm:mb-6">Statistiques agrégées et anonymisées sur le suivi digital de la freination myopique.</p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-5 sm:mb-6">
        <KPI label="Patients suivis" value={stats.total_patients} />
        <KPI label="Étapes réalisées" value={stats.steps_done} />
        <KPI label="Étapes en attente" value={stats.steps_pending} accent />
        <KPI label="Taux complétion" value={`${stats.completion_rate_avg}%`} sub={`Âge moyen ${stats.avg_age} ans`} />
        <div
          className="bg-white rounded-xl border border-[#e5e9f1] p-4 sm:p-5"
          data-testid="myo-kpi-temps-moyen-/-dépôt"
        >
          <div className="text-[10px] sm:text-[11px] uppercase tracking-wider text-[#3C66AE]">
            Temps moyen / dépôt
          </div>
          <div className="mt-2 flex items-end justify-between gap-2">
            <div className="text-2xl sm:text-3xl font-semibold text-[#172343]">
              {fmtDuration(stats.avg_session_seconds || 113)}
            </div>
            {Array.isArray(sessionHistory) && sessionHistory.length >= 2 && (
              <Sparkline data={sessionHistory} positiveTrend="down" />
            )}
          </div>
          {sessionDeltaPct !== 0 && (
            <div
              className={`text-[11px] mt-1 font-medium ${
                sessionDeltaPct < 0 ? "text-[#1FB585]" : "text-[#ED761E]"
              }`}
              data-testid="myo-kpi-session-delta"
            >
              {sessionDeltaPct < 0 ? "▼" : "▲"} {Math.abs(sessionDeltaPct)}% sur 4 semaines
            </div>
          )}
          <div className="text-[11px] text-[#3C66AE]/70 mt-0.5">Durée d'utilisation client</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
        <DistCard title="Répartition par sexe" data={Object.entries(stats.by_sex).map(([k, v]) => ({ label: k, count: v }))} />
        <DistCard title="Année de naissance" data={stats.by_birth_year.map((y) => ({ label: String(y.year), count: y.count }))} />
        <DistCard title="Solution / produits" data={stats.by_solution.map((s) => ({ label: s.label, count: s.count }))} />
      </div>
      <div className="bg-white rounded-xl border border-[#e5e9f1] p-4 sm:p-5">
        <div className="text-[12px] font-medium text-[#172343] mb-3 inline-flex items-center gap-2"><Activity size={14} />Avancement par étape du protocole</div>
        <div className="grid gap-2">
          {(stats.steps_by_etape || []).map((e) => {
            const pct = e.total > 0 ? Math.round((e.done / e.total) * 100) : 0;
            return (
              <div key={e.code} className="grid grid-cols-[80px_1fr_60px] sm:grid-cols-[120px_1fr_70px] gap-2 sm:gap-3 items-center text-[11px] sm:text-[12px]">
                <span className="text-[#172343] font-mono">{e.code}</span>
                <div className="h-3 bg-[#e5e9f1] rounded-full overflow-hidden relative">
                  <div className="h-full bg-[#3C66AE]" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-right text-[#3C66AE] font-mono">{e.done}/{e.total}</span>
              </div>
            );
          })}
          {(!stats.steps_by_etape || stats.steps_by_etape.length === 0) && (
            <div className="text-[12px] text-[#3C66AE]/60 italic">Aucune donnée</div>
          )}
        </div>
      </div>

      {/* ============== ROADMAP : Évolutions disponibles à la demande ============== */}
      <RoadmapSection />
    </div>
  );
}

function RoadmapSection() {
  const packs = [
    {
      icon: <TrendingDown size={18} />,
      title: "Engagement & adoption",
      tag: "Pack 1",
      tone: "blue",
      items: [
        "Taux de complétion du protocole à 30, 60 et 90 jours",
        "Taux d'observance (suivi du calendrier sans saut d'étape)",
        "Taux d'abandon vs comparatif suivi non-digital",
        "Délai moyen entre chaque étape du protocole",
        "Time-to-first-value : délai 1ère consultation → 1ère mesure",
      ],
    },
    {
      icon: <Award size={18} />,
      title: "Performance médicale",
      tag: "Pack 2",
      tone: "mint",
      items: [
        "% de patients dans les objectifs thérapeutiques (-0.50 dpt/an)",
        "Évolution moyenne SE et longueur axiale par cohorte",
        "Distribution enrichie des fabricants et part de marché",
        "Taux de switch entre solutions (Stellest ↔ MiYosmart ↔ MyoCare)",
        "Comparatif vs courbe d'évolution naturelle non traitée",
      ],
    },
    {
      icon: <Map size={18} />,
      title: "Couverture territoriale",
      tag: "Pack 3",
      tone: "blue",
      items: [
        "Carte de France interactive : densité de praticiens actifs",
        "Top 10 villes (Paris, Lyon, Marseille…) en volume de dépôts",
        "Nombre de praticiens uniques actifs par département",
        "Top 5 ophtalmologues / opticiens prescripteurs (anonymisés)",
        "Évolution mensuelle de la couverture territoriale",
      ],
    },
    {
      icon: <Shield size={18} />,
      title: "Qualité & confiance",
      tag: "Pack 4",
      tone: "mint",
      items: [
        "Taux de complétude des dossiers (INS, examens, observations)",
        "Taux d'utilisation DMP / Mon Espace Santé",
        "% de dossiers signés numériquement",
        "NPS / satisfaction praticien (sondage post-utilisation)",
        "Indicateur de fraîcheur des données par patient",
      ],
    },
  ];

  return (
    <div className="mt-6 sm:mt-8" data-testid="myo-roadmap-section">
      {/* Bandeau d'introduction */}
      <div className="bg-gradient-to-br from-[#0F1F3A] to-[#1A3260] rounded-xl p-5 sm:p-6 mb-4 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-[#3C66AE]/10 rounded-full blur-3xl -translate-y-10 translate-x-10 pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-[#3C66AE]/20 border border-[#7FB3FF]/30 flex items-center justify-center">
              <Sparkles size={14} className="text-[#7FB3FF]" />
            </div>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#7FB3FF] font-medium">
              Évolutions disponibles à la demande
            </span>
          </div>
          <h2 className="text-[18px] sm:text-[22px] font-semibold mb-2">
            Activez les analyses avancées pour votre cohorte
          </h2>
          <p className="text-[13px] sm:text-[14px] text-white/80 leading-relaxed max-w-3xl">
            Après <strong className="text-white">validation officielle de votre commande</strong>,
            votre espace Myo-Control peut être enrichi à la demande avec les indicateurs ci-dessous.
            Tous sont calculés à partir de données <strong className="text-white">anonymisées</strong> et
            conformes aux référentiels nationaux (INS, RGPD, hébergement HDS).
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-white/60">
            <Lock size={12} /> Activation par MetaConnect · Délai moyen 5 jours ouvrés
          </div>
        </div>
      </div>

      {/* Grille des packs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {packs.map((p) => {
          const accentBg = p.tone === "mint" ? "bg-[#1FB585]/10 border-[#1FB585]/25 text-[#1FB585]" : "bg-[#3C66AE]/10 border-[#3C66AE]/25 text-[#3C66AE]";
          return (
            <div
              key={p.tag}
              className="bg-white rounded-xl border border-[#e5e9f1] p-5 hover:border-[#3C66AE]/40 transition"
              data-testid={`roadmap-pack-${p.tag.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-full border flex items-center justify-center ${accentBg}`}>
                    {p.icon}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#94a0b6] font-medium">
                      {p.tag}
                    </div>
                    <div className="text-[15px] font-semibold text-[#172343]">{p.title}</div>
                  </div>
                </div>
              </div>
              <ul className="space-y-1.5">
                {p.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-[12.5px] text-[#3C66AE] leading-relaxed">
                    <Check size={13} className="text-[#1FB585] mt-1 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Footer CTA discret */}
      <div className="mt-4 text-center text-[12px] text-[#3C66AE]/70">
        Pour activer un ou plusieurs de ces modules, contactez votre interlocuteur MetaConnect ou écrivez à{" "}
        <a href="mailto:contact@metaconnect.fr" className="text-[#3C66AE] underline hover:text-[#172343]">
          contact@metaconnect.fr
        </a>
      </div>
    </div>
  );
}

function DistCard({ title, data }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="bg-white rounded-xl border border-[#e5e9f1] p-5">
      <div className="text-[12px] font-medium text-[#172343] mb-3">{title}</div>
      {data.length === 0 ? (
        <div className="text-[12px] text-[#3C66AE]/60 italic">Aucune donnée</div>
      ) : (
        <div className="grid gap-2">
          {data.slice(0, 8).map((d) => (
            <div key={d.label} className="grid grid-cols-[80px_1fr_40px] gap-2 items-center text-[12px]">
              <span className="text-[#172343] truncate" title={d.label}>{d.label}</span>
              <div className="h-2 bg-[#e5e9f1] rounded-full overflow-hidden">
                <div className="h-full bg-[#3C66AE]" style={{ width: `${(d.count / max) * 100}%` }} />
              </div>
              <span className="text-right text-[#3C66AE] font-mono">{d.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Patients list + detail drawer ---------------- */
function Patients() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ sexe: "", search: "" });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await axios.get(`${apiBase}/patients`, { params });
      setData(data);
    } finally { setBusy(false); }
  }, [filters]);
  useEffect(() => { reload(); }, [reload]);

  if (selected) return <PatientView code={selected} onBack={() => setSelected(null)} />;

  return (
    <div data-testid="myo-patients">
      <h1 className="text-2xl font-semibold text-[#172343] mb-1">Patients suivis</h1>
      <p className="text-[13px] text-[#3C66AE] mb-6">{data.total} patient(s) · données pré-anonymisées (initiales 2 lettres + année naissance).</p>
      <div className="bg-white rounded-xl border border-[#e5e9f1] mb-4 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div><label className="text-[10px] uppercase tracking-wider text-[#3C66AE]">Sexe</label>
          <select value={filters.sexe} onChange={(e) => setFilters({ ...filters, sexe: e.target.value })}
            data-testid="myo-filter-sex"
            className="w-full mt-1 px-2 py-1.5 border border-[#e5e9f1] rounded text-[13px]">
            <option value="">Tous</option><option value="F">Femme</option><option value="M">Homme</option>
          </select>
        </div>
        <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-wider text-[#3C66AE]">Recherche code patient</label>
          <input value={filters.search} placeholder="MYO-…"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="myo-filter-search"
            className="w-full mt-1 px-2 py-1.5 border border-[#e5e9f1] rounded text-[13px]" />
        </div>
        <button onClick={() => setFilters({ sexe: "", search: "" })}
          className="text-[12px] text-[#3C66AE] hover:underline self-end sm:pb-1 text-left sm:text-center">Réinitialiser</button>
      </div>

      {/* Mobile: card layout */}
      <div className="md:hidden grid gap-3">
        {busy && <div className="p-6 text-center text-[#3C66AE]"><Loader2 size={16} className="animate-spin inline" /></div>}
        {!busy && data.items.length === 0 && (
          <div className="p-12 text-center text-[#3C66AE]/60 italic">Aucun patient.</div>
        )}
        {!busy && data.items.map((p) => (
          <button key={p.patient_code} onClick={() => setSelected(p.patient_code)}
            data-testid={`myo-patient-card-${p.patient_code}`}
            className="bg-white rounded-xl border border-[#e5e9f1] p-4 text-left hover:bg-[#f5f7fb] active:bg-[#e5e9f1] transition">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[13px] text-[#172343] font-semibold">{p.patient_code}</div>
                <div className="text-[12px] text-[#3C66AE]">{p.prenom_2l}.{p.nom_2l}. · {p.annee_naissance || "—"} · {p.sexe || "—"}</div>
              </div>
              <Eye size={14} className="text-[#3C66AE] mt-1" />
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-[#3C66AE]">
                <span className="font-mono text-emerald-700">{p.nb_done}</span>
                <span className="font-mono">/{p.nb_etapes}</span>
                <span className="ml-2 font-mono">{p.taux_completion}%</span>
              </span>
              {p.prochaine_etape && (
                <span className="text-[#3C66AE]/70 truncate ml-2">→ {p.prochaine_etape}</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Desktop/tablet: table layout */}
      <div className="hidden md:block bg-white rounded-xl border border-[#e5e9f1] overflow-x-auto">
        <table className="w-full text-[13px] min-w-[700px]">
          <thead className="bg-[#f5f7fb] text-[10px] uppercase tracking-wider text-[#3C66AE]">
            <tr>
              <th className="text-left p-3">Code</th><th className="text-left p-3">Initiales</th>
              <th className="text-left p-3">Année naiss.</th><th className="text-left p-3">Sexe</th>
              <th className="text-left p-3">Solution</th><th className="text-left p-3">Étapes</th>
              <th className="text-left p-3">Complétion</th><th className="text-left p-3">Prochaine étape</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {busy && <tr><td colSpan={9} className="p-6 text-center text-[#3C66AE]"><Loader2 size={16} className="animate-spin inline" /></td></tr>}
            {!busy && data.items.length === 0 && (
              <tr><td colSpan={9} className="p-12 text-center text-[#3C66AE]/60 italic">Aucun patient.</td></tr>
            )}
            {!busy && data.items.map((p) => (
              <tr key={p.patient_code} className="border-t border-[#e5e9f1] hover:bg-[#f5f7fb] cursor-pointer"
                onClick={() => setSelected(p.patient_code)}
                data-testid={`myo-patient-${p.patient_code}`}>
                <td className="p-3 font-mono text-[#172343]">{p.patient_code}</td>
                <td className="p-3 text-[#172343]">{p.prenom_2l}.{p.nom_2l}.</td>
                <td className="p-3">{p.annee_naissance || "—"}</td>
                <td className="p-3">{p.sexe || "—"}</td>
                <td className="p-3 text-[12px] text-[#3C66AE] truncate max-w-[160px]">{p.produits_solution || "—"}</td>
                <td className="p-3 text-[12px]"><span className="font-mono text-emerald-700">{p.nb_done}</span> / <span className="font-mono">{p.nb_etapes}</span></td>
                <td className="p-3"><span className="font-mono">{p.taux_completion}%</span></td>
                <td className="p-3 text-[11px] text-[#3C66AE]/80 truncate max-w-[200px]" title={p.prochaine_etape}>{p.prochaine_etape || "—"}</td>
                <td className="p-3 text-right"><Eye size={14} className="text-[#3C66AE]" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- PatientView (full-page, OhMyVue/Stellest layout) ---------------- */
function PatientView({ code, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const { data } = await axios.get(`${apiBase}/patients/${code}`); if (alive) setData(data); }
      catch (e) { if (alive) setErr(e?.response?.data?.detail || "Erreur"); }
    })();
    return () => { alive = false; };
  }, [code]);

  if (err) return <div className="text-red-600">{err}</div>;
  if (!data) return <div className="text-[#3C66AE]"><Loader2 size={20} className="animate-spin inline mr-2" />Chargement…</div>;

  const p = data.patient;
  const nbDone = data.steps.filter((s) => s.statut === "done").length;
  const nbTotal = data.steps.length || 1;
  const completion = Math.round((nbDone / nbTotal) * 100);

  return (
    <div data-testid="myo-patient-view" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[22px] sm:text-[26px] font-semibold text-[#172343] tracking-tight break-words">
            {p.prenom_2l}.{p.nom_2l}. <span className="text-[#3C66AE]/60 text-[15px] sm:text-[18px]">— {p.patient_code}</span>
          </h1>
          <p className="text-[12px] sm:text-[13px] text-[#64748b] mt-0.5">Stellest® (Essilor) · Suivi 24 mois · Données pré-anonymisées</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href="/docs/passeport-stellest.pdf" target="_blank" rel="noopener noreferrer"
            data-testid="myo-passport-full-link"
            className="px-3 py-1.5 rounded-lg border border-[#e5e9f1] bg-white text-[#172343] text-[13px] hover:bg-[#f5f7fb] inline-flex items-center gap-1.5"
            title="Ouvrir le passeport Stellest complet (PDF)">
            <FileQuestion size={14} />Passeport Stellest
          </a>
          <button onClick={onBack} data-testid="myo-patient-back"
            className="px-3 py-1.5 rounded-lg border border-[#e5e9f1] bg-white text-[#172343] text-[13px] hover:bg-[#f5f7fb] inline-flex items-center gap-1.5">
            <ArrowLeft size={14} />Retour à la liste
          </button>
        </div>
      </div>

      <div>
        <h2 className="text-[18px] font-semibold text-[#172343]">Freination de la myopie</h2>
        <p className="text-[13px] text-[#64748b]">Suivi structuré et historisé du patient.</p>
      </div>

      {/* Section 1: Informations générales */}
      <SectionCard title="Informations générales">
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
          <Field label="Initiales" value={`${p.prenom_2l}.${p.nom_2l}.`} />
          <Field label="Année de naissance" value={p.annee_naissance || "—"} />
          <Field label="Sexe" value={p.sexe || "—"} />
          <Field label="Solution / produits" value={p.produits_solution || "—"} />
          <Field label="Indices" value={p.indices || "—"} />
          <Field label="Suivi orthoptiste" value={p.suivi_orthoptiste || "—"} />
          <Field label="Myopie parent 1" value={p.myopie_parent_1 || "—"} />
          <Field label="Myopie parent 2" value={p.myopie_parent_2 || "—"} />
          <Field label="Antécédents familiaux" value={p.antecedents_familiaux || "—"} />
          <Field label="Autre solution myopie" value={p.autre_solution_myopie || "—"} />
        </div>
        <div className="mt-5 pt-5 border-t border-[#e5e9f1]">
          <div className="text-[11px] uppercase tracking-wider text-[#64748b] mb-2 font-medium">Engagement digital & DMP</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Flag label="Mail protocole envoyé" value={p.mail_protocole_envoye} />
            <Flag label="Dépôt DMP" value={p.depot_dmp} />
            <Flag label="Espace OhMyVue ouvert" value={p.ouverture_espace_ohmyvue} />
            <Flag label="Email suivi consulté" value={p.consultation_email_suivi} />
          </div>
        </div>
      </SectionCard>

      {/* Section : Habitudes de vie (au niveau patient) */}
      {data.behaviors && data.behaviors.length > 0 && (
        <SectionCard title="Habitudes de vie">
          <p className="text-[12px] text-[#64748b] mb-3">Données déclarées par le patient et son entourage. Saisie libre durant les visites.</p>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
            {data.behaviors.map((b, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#e5e9f1]/60 last:border-0">
                <div>
                  <div className="text-[13px] text-[#172343]">{b.activite_label || b.activite_key}</div>
                  {b.commentaire_qualite && (
                    <div className="text-[11px] text-[#64748b] italic">{b.commentaire_qualite}</div>
                  )}
                </div>
                <div className="text-right">
                  {b.heures_par_jour !== null && b.heures_par_jour !== undefined && (
                    <span className="text-[14px] font-mono text-[#172343] font-medium">{b.heures_par_jour} h/j</span>
                  )}
                  {b.lunettes_portees && (
                    <div className="text-[10px] uppercase tracking-wider text-[#64748b]">Lunettes : {b.lunettes_portees}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Section 2: Planning théorique */}
      <SectionCard title="Planning théorique">
        <div className="grid">
          {data.steps.map((s) => (
            <div key={s.step_uid}
              className="flex items-start justify-between gap-4 py-3 border-b border-[#e5e9f1] last:border-0">
              <div className="flex-1 min-w-0">
                <div className="text-[14px] text-[#172343] font-medium">{s.libelle_etape}</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <StatusBadge status={s.statut} />
                  <span className="text-[12px] text-[#64748b]">
                    {s.acteur_attendu === "optician" ? "Opticien" : s.acteur_attendu === "ophthalmologist" ? "Ophtalmologiste" : s.acteur_attendu || "—"}
                  </span>
                  {s.date_realisation && (
                    <span className="text-[11px] text-[#64748b]">· réalisé {s.date_realisation}</span>
                  )}
                </div>
              </div>
              <div className="text-[13px] text-[#172343] font-mono whitespace-nowrap pt-1">
                {s.date_realisation || s.date_planifiee || s.date_prevue || "—"}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Section 3: Progression du protocole */}
      <SectionCard title="Progression du protocole">
        <div className="flex items-center justify-between text-[13px] mb-2">
          <span className="text-[#172343] font-medium">Étape {nbDone} / {nbTotal}</span>
          <span className="text-[#172343] font-mono">{completion}%</span>
        </div>
        <div className="h-2.5 w-full bg-[#e5e9f1] rounded-full overflow-hidden">
          <div className="h-full bg-[#172343] transition-all duration-500" style={{ width: `${completion}%` }} />
        </div>
      </SectionCard>

      {/* Section 4: Historique visuel (4 charts) */}
      <SectionCard title="Historique visuel">
        <div className="grid md:grid-cols-2 gap-4">
          <MeasurementChart label="Allongement de l'œil OD" oeil="OD" measurements={data.measurements} field="longueur_axiale_mm" unit="mm" />
          <MeasurementChart label="Allongement de l'œil OG" oeil="OG" measurements={data.measurements} field="longueur_axiale_mm" unit="mm" />
          <MeasurementChart label="Évolution de la myopie OD" oeil="OD" measurements={data.measurements} field="sphere" unit="D" anchorZero />
          <MeasurementChart label="Évolution de la myopie OG" oeil="OG" measurements={data.measurements} field="sphere" unit="D" anchorZero />
        </div>
      </SectionCard>

      {/* Section 5: Historique des étapes (done) */}
      <SectionCard title="Historique des étapes">
        {data.steps.filter((s) => s.statut === "done").length === 0 ? (
          <div className="text-[13px] text-[#64748b] italic text-center py-8">Aucune étape réalisée pour le moment.</div>
        ) : (
          <div className="grid gap-5">
            {data.steps.filter((s) => s.statut === "done").map((s) => (
              <StepHistoryCard key={s.step_uid} step={s} measurements={data.measurements} answers={data.answers}
                pro_observations={data.pro_observations} dmp_responses={data.dmp_responses} />
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <section className="bg-white rounded-xl border border-[#e5e9f1] overflow-hidden">
      <header className="px-5 py-3 border-b border-[#e5e9f1]">
        <h3 className="text-[15px] font-semibold text-[#172343]">{title}</h3>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#64748b] font-medium">{label}</div>
      <div className="text-[14px] text-[#172343] mt-0.5">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    done: { label: "Réalisé", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    pending: { label: "À venir", className: "bg-amber-50 text-amber-700 border-amber-200" },
    late: { label: "En retard", className: "bg-red-50 text-red-700 border-red-200" },
  };
  const cfg = map[status] || { label: status || "—", className: "bg-[#f5f7fb] text-[#3C66AE] border-[#e5e9f1]" };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function MeasurementChart({ label, oeil, measurements, field, unit, anchorZero }) {
  const points = useMemo(() => {
    return (measurements || [])
      .filter((m) => m.oeil === oeil && m[field] !== null && m[field] !== undefined && m.date_mesure)
      .map((m) => ({
        date: m.date_mesure, code: m.code_etape, value: Number(m[field]),
      }))
      .sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [measurements, oeil, field]);

  // Y domain : when anchorZero (sphère), clamp to [floor(min)-0.5, 0] with 0 at bottom (reversed).
  const domain = useMemo(() => {
    if (!anchorZero || points.length === 0) return ["auto", "auto"];
    const minVal = Math.min(...points.map((p) => p.value));
    // floor to next 0.5 below min so line never touches top edge
    const lower = Math.floor((minVal - 0.25) * 2) / 2;
    return [lower, 0];
  }, [anchorZero, points]);

  return (
    <div className="bg-[#f9fafc] border border-[#e5e9f1] rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] text-[#172343] font-medium">{label}</div>
        <div className="text-[10px] uppercase tracking-wider text-[#64748b]">{oeil}</div>
      </div>
      {points.length === 0 ? (
        <div className="h-[160px] flex items-center justify-center text-[12px] text-[#64748b] italic">Aucune donnée</div>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={points} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f1" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} stroke="#cbd3e1" />
            <YAxis tick={{ fontSize: 10, fill: "#64748b" }} stroke="#cbd3e1"
              reversed={!!anchorZero} domain={domain}
              label={{ value: unit, angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#64748b" } }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e9f1" }}
              formatter={(v) => [`${v} ${unit}`, label]} labelFormatter={(d) => `Date : ${d}`} />
            <ReferenceLine y={0} stroke="#cbd3e1" />
            <Line type="monotone" dataKey="value" stroke="#172343" strokeWidth={2}
              dot={{ r: 4, fill: "#ED761E", strokeWidth: 0 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/* Map Myo-Control step.code_etape → page in Stellest passport PDF.
   Derived from observed DMP/observations data. Falls back to 1 if unknown. */
const PASSPORT_PAGE_MAP = {
  initial: 11,        // J0 — remise équipement
  "15d": 13,          // J+15 — visite opticien
  "2m": 16,           // 2 mois — suivi ophtalmologique
  "6m_opt": 17,       // 6 mois — visite opticien
  "6m_oph": 19,       // 6 mois — suivi ophtalmologique
  "12m_opt": 20,      // 12 mois — visite opticien
  "12m_oph": 22,      // 12 mois — suivi ophtalmologique
  "18m": 23,          // 18 mois — visite opticien
  "24m": 24,          // 24 mois — bilan final
};

/* Map step.code_etape → list of `etape` labels used in DMP responses & pro observations.
   Used to attach DMP/observation rows to the right step. */
const ETAPE_DMP_MAP = {
  initial: ["j0 - remise équipement", "prescription initiale"],
  "15d": ["j+15 - visite opticien"],
  "2m": ["2 mois - suivi ophtalmologique"],
  "6m_opt": ["6 mois - visite opticien"],
  "6m_oph": ["6 mois - suivi ophtalmologique"],
  "12m_opt": ["12 mois - visite opticien"],
  "12m_oph": ["12 mois - suivi ophtalmologique"],
  "18m": ["18 mois - visite opticien"],
  "24m": ["24 mois - bilan final"],
};

function rowsForStep(rows, step) {
  if (!rows || !step) return [];
  const map = ETAPE_DMP_MAP[step.code_etape] || [];
  return rows.filter((r) => {
    if (!r.etape) return false;
    const e = r.etape.toLowerCase();
    return map.some((m) => e === m || e.startsWith(m));
  });
}

function passportPageForStep(step, dmp_responses, pro_observations) {
  // 1. Try direct DMP/observation match (server-derived page)
  const allRefs = [...(dmp_responses || []), ...(pro_observations || [])];
  const exact = allRefs.find(
    (r) => r.etape && step.libelle_etape && r.etape.split(" - ")[0].toLowerCase() === step.libelle_etape.split(" — ")[0].toLowerCase()
  );
  if (exact?.page_pdf) return exact.page_pdf;
  // 2. Fallback to static map
  return PASSPORT_PAGE_MAP[step.code_etape] || 1;
}

function StepHistoryCard({ step, measurements, answers, pro_observations, dmp_responses }) {
  const stepMeasurements = (measurements || []).filter((m) => m.step_uid === step.step_uid);
  const stepAnswers = (answers || []).filter((a) => a.step_uid === step.step_uid);
  const stepObs = rowsForStep(pro_observations, step);
  const stepDmp = rowsForStep(dmp_responses, step);
  const passportPage = passportPageForStep(step, dmp_responses, pro_observations);
  const byCategory = stepAnswers.reduce((acc, a) => {
    const k = a.categorie || "Questionnaire";
    if (!acc[k]) acc[k] = [];
    acc[k].push(a);
    return acc;
  }, {});

  return (
    <article className="bg-white border border-[#e5e9f1] rounded-lg overflow-hidden">
      <header className="px-4 py-3 border-b border-[#e5e9f1] flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <div className="text-[14px] text-[#172343] font-semibold">{step.libelle_etape}</div>
          <div className="text-[11px] text-[#64748b] uppercase tracking-wider mt-0.5">Étape {step.ordre}</div>
          <div className="text-[12px] text-[#64748b] mt-1">
            {step.date_realisation || step.date_planifiee || step.date_prevue || "—"}
            {step.acteur_attendu && ` · ${step.acteur_attendu === "optician" ? "Opticien" : step.acteur_attendu === "ophthalmologist" ? "Ophtalmologiste" : step.acteur_attendu}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={`/docs/passeport-stellest.pdf#page=${passportPage}`} target="_blank" rel="noopener noreferrer"
            data-testid={`myo-passport-link-${step.code_etape}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#172343] bg-[#f5f7fb] hover:bg-[#e5e9f1] border border-[#e5e9f1] transition"
            title={`Voir page ${passportPage} du passeport Stellest`}>
            <FileQuestion size={12} />
            Passeport p.{passportPage}
          </a>
          <StatusBadge status={step.statut} />
        </div>
      </header>
      <div className="p-4 space-y-4">
        {/* Tableau OD/OG */}
        {stepMeasurements.length > 0 && (
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <table className="w-full text-[12px] min-w-[480px]">
            <thead>
              <tr className="bg-[#172343] text-white text-[10px] uppercase tracking-wider">
                <th className="text-left p-2 font-medium"></th>
                <th className="text-center p-2 font-medium">SPH (D)</th>
                <th className="text-center p-2 font-medium">CYL (D)</th>
                <th className="text-center p-2 font-medium">AXE (°)</th>
                <th className="text-center p-2 font-medium">AV</th>
                <th className="text-center p-2 font-medium">AXIALE (mm)</th>
              </tr>
            </thead>
            <tbody>
              {["OD", "OG"].map((eye) => {
                const m = stepMeasurements.find((mm) => mm.oeil === eye) || {};
                return (
                  <tr key={eye} className="border-b border-[#e5e9f1] last:border-0">
                    <td className="p-2.5 font-medium text-emerald-700">{eye}</td>
                    <td className="p-2.5 text-center font-mono text-[#172343]">{m.sphere ?? "—"} {m.sphere ? "D" : ""}</td>
                    <td className="p-2.5 text-center font-mono text-[#172343]">{m.cylindre ?? "—"} {m.cylindre ? "D" : ""}</td>
                    <td className="p-2.5 text-center font-mono text-[#172343]">{m.axe ?? "—"}{m.axe ? "°" : ""}</td>
                    <td className="p-2.5 text-center font-mono text-[#172343]">{m.acuite_visuelle ?? "—"}</td>
                    <td className="p-2.5 text-center font-mono text-[#172343]">{m.longueur_axiale_mm ?? "—"} {m.longueur_axiale_mm ? "mm" : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        {/* Questionnaire */}
        {Object.keys(byCategory).length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#64748b] font-medium mb-2">Questionnaire</div>
            {Object.entries(byCategory).map(([cat, items]) => (
              <div key={cat} className="mb-3 last:mb-0">
                <div className="text-[12px] text-[#172343] font-medium mb-1.5">{cat}</div>
                {items.map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#e5e9f1]/60 last:border-0">
                    <span className="text-[12px] text-[#172343]">{a.question_label || a.question_key}</span>
                    {a.valeur ? (
                      <SmileyBadge value={a.valeur} />
                    ) : (
                      <span className="text-[11px] text-[#64748b] italic">(non renseigné)</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Réponses passeport DMP (extraites du PDF) */}
        {stepDmp.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#64748b] font-medium mb-2">Réponses passeport DMP</div>
            <div className="grid gap-1.5">
              {stepDmp.map((r, i) => (
                <div key={i} className="flex items-start justify-between gap-3 py-1.5 border-b border-[#e5e9f1]/60 last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-[#172343]">{r.question_label || r.question_key}</div>
                    {r.acteur && (
                      <span className="text-[10px] uppercase tracking-wider text-[#64748b]">{r.acteur} · page {r.page_pdf || "?"}</span>
                    )}
                  </div>
                  <span className="text-[12px] font-mono text-[#172343] font-medium whitespace-nowrap">
                    {r.reponse_texte || "—"}
                    {r.reponse_score_normalise !== null && r.reponse_score_normalise !== undefined && (
                      <span className="text-[10px] text-[#64748b] ml-1">({r.reponse_score_normalise})</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Observations cliniques */}
        {stepObs.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#64748b] font-medium mb-2">Observations / Commentaires</div>
            <div className="grid gap-2">
              {stepObs.map((o, i) => (
                <div key={i} className={`p-3 rounded-md text-[12px] border-l-4 ${
                  o.acteur === "opticien" ? "bg-pink-50 border-pink-400" :
                  o.acteur === "ophtalmologiste" ? "bg-cyan-50 border-cyan-400" :
                  "bg-[#f5f7fb] border-[#3C66AE]"
                }`}>
                  <div className="text-[10px] uppercase tracking-wider font-medium text-[#172343]">{o.type_observation} — {o.acteur}</div>
                  <div className="text-[#172343] italic mt-1">"{o.contenu}"</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function SmileyBadge({ value }) {
  const v = String(value || "").trim().toLowerCase();
  const cfg = v.includes("très bon") || v.includes("tres bon")
    ? { label: "Très bon", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" }
    : v === "bon"
    ? { label: "Bon", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
    : v === "moyen"
    ? { label: "Moyen", cls: "bg-amber-50 text-amber-700 border-amber-200" }
    : { label: value, cls: "bg-[#f5f7fb] text-[#172343] border-[#e5e9f1]" };
  return (
    <span className={`inline-flex items-center text-[11px] font-medium px-2.5 py-0.5 rounded-md border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function Flag({ label, value }) {
  const v = (value || "").toLowerCase();
  const styles = v === "oui"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : v === "non"
    ? "bg-red-50 text-red-700 border-red-200"
    : "bg-[#f5f7fb] text-[#3C66AE] border-[#e5e9f1]";
  const icon = v === "oui" ? "✓" : v === "non" ? "✕" : "?";
  return (
    <div className={`px-3 py-2 rounded-lg border text-[12px] ${styles}`}>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[14px]">{icon}</span>
        <span className="font-medium">{label}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wider mt-0.5 opacity-80">{value || "non renseigné"}</div>
    </div>
  );
}

/* ---------------- Governance (Questions + Champs) ---------------- */
function Governance({ user }) {
  const [tab, setTab] = useState("questions");
  return (
    <div data-testid="myo-governance">
      <h1 className="text-2xl font-semibold text-[#172343] mb-1">Gouvernance des données</h1>
      <p className="text-[13px] text-[#3C66AE] mb-6">Annotez les questions et champs CRM pour faire évoluer la collecte avec MetaConnect.</p>
      <div className="bg-white rounded-xl border border-[#e5e9f1]">
        <div className="border-b border-[#e5e9f1] px-5 flex">
          {[{ k: "questions", l: "Questions du protocole", i: FileQuestion },
            { k: "fields", l: "Champs CRM", i: Database }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`myo-gov-tab-${t.k}`}
              className={`py-3 px-4 text-[13px] border-b-2 transition inline-flex items-center gap-2 ${
                tab === t.k ? "border-[#ED761E] text-[#ED761E]" : "border-transparent text-[#3C66AE] hover:text-[#172343]"
              }`}>
              <t.i size={14} />{t.l}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === "questions" && <GovernanceQuestions canAnnotate={user.role === "industrial"} />}
          {tab === "fields" && <GovernanceFields canAnnotate={user.role === "industrial"} />}
        </div>
      </div>
    </div>
  );
}

function GovernanceQuestions({ canAnnotate }) {
  const [questions, setQuestions] = useState([]);
  const [err, setErr] = useState("");
  const reload = useCallback(async () => {
    try { const { data } = await axios.get(`${apiBase}/governance/questions`); setQuestions(data); }
    catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const annotate = async (key, decision, priority, comment) => {
    try {
      await axios.post(`${apiBase}/governance/questions/${key}/annotation`, { decision, priority, comment });
      reload();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="grid gap-3">
      {err && <div className="text-red-600">{err}</div>}
      <div className="text-[12px] text-[#3C66AE]/80">{questions.length} question(s) du protocole.</div>
      {questions.map((q) => (
        <QuestionCard key={q.question_key} q={q} canAnnotate={canAnnotate} onSave={annotate} />
      ))}
    </div>
  );
}

function QuestionCard({ q, canAnnotate, onSave }) {
  const [open, setOpen] = useState(false);
  const ann = q.annotation;
  const [decision, setDecision] = useState(ann?.decision || "");
  const [priority, setPriority] = useState(ann?.priority || "moyenne");
  const [comment, setComment] = useState(ann?.comment || "");
  const decisions = [
    { v: "keep_required", l: "Conserver obligatoire" }, { v: "make_required", l: "Rendre obligatoire" },
    { v: "make_optional", l: "Rendre facultatif" }, { v: "delete", l: "Supprimer" },
    { v: "reformulate", l: "Reformuler" }, { v: "add_similar", l: "Ajouter une question similaire" },
  ];
  return (
    <div className="bg-[#f9fafc] rounded-lg border border-[#e5e9f1] p-4" data-testid={`myo-q-${q.question_key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] uppercase tracking-wider text-[#3C66AE] font-mono">{q.question_key}</span>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#172343]/10 text-[#172343]">{q.categories || "—"}</span>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#3C66AE]/10 text-[#3C66AE]">{q.statut_actuel || "—"}</span>
            {ann && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Annoté ({ann.decision})</span>}
          </div>
          <div className="text-[14px] text-[#172343] font-medium">{q.question_label}</div>
          <div className="text-[11px] text-[#3C66AE]/70 mt-1">
            Étapes : {q.etapes_concernees || "?"} · Type : {q.type_reponse || "?"} · Intérêt : {q.interet_data_estime || "?"} · Remplissage : {Math.round((q.taux_remplissage || 0) * 100)}% ({q.nb_reponses}/{q.nb_occurrences})
          </div>
        </div>
        {canAnnotate && (
          <button onClick={() => setOpen(!open)}
            data-testid={`myo-q-${q.question_key}-toggle`}
            className="px-3 py-1.5 rounded-lg text-[12px] font-medium bg-[#172343] text-white hover:bg-[#0F1A33] inline-flex items-center gap-1">
            {ann ? "Modifier" : "Annoter"} <ChevronDown size={12} className={open ? "rotate-180" : ""} />
          </button>
        )}
      </div>
      {open && canAnnotate && (
        <div className="mt-4 pt-4 border-t border-[#e5e9f1] grid gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#3C66AE]">Décision</label>
            <select value={decision} onChange={(e) => setDecision(e.target.value)}
              data-testid={`myo-q-${q.question_key}-decision`}
              className="w-full mt-1 px-2 py-1.5 border border-[#e5e9f1] rounded text-[13px]">
              <option value="">Choisir…</option>
              {decisions.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#3C66AE]">Priorité</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 border border-[#e5e9f1] rounded text-[13px]">
              <option value="haute">Haute</option><option value="moyenne">Moyenne</option><option value="basse">Basse</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#3C66AE]">Commentaire</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
              data-testid={`myo-q-${q.question_key}-comment`}
              className="w-full mt-1 px-2 py-1.5 border border-[#e5e9f1] rounded text-[13px] resize-y" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-[12px] text-[#3C66AE]">Annuler</button>
            <button onClick={() => { if (decision) { onSave(q.question_key, decision, priority, comment); setOpen(false); } }}
              data-testid={`myo-q-${q.question_key}-save`} disabled={!decision}
              className="px-4 py-1.5 rounded-lg text-[12px] bg-[#ED761E] hover:bg-[#D86813] text-white disabled:opacity-50">Enregistrer</button>
          </div>
        </div>
      )}
    </div>
  );
}

function GovernanceFields({ canAnnotate }) {
  const [fields, setFields] = useState([]);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const reload = useCallback(async () => {
    try { const { data } = await axios.get(`${apiBase}/governance/fields`); setFields(data); }
    catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const annotate = async (table, champ, body) => {
    try {
      await axios.post(`${apiBase}/governance/fields/${encodeURIComponent(table)}/${encodeURIComponent(champ)}/annotation`, body);
      reload();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  };

  const tables = [...new Set(fields.map((f) => f.table_crm))];
  const filtered = filter ? fields.filter((f) => f.table_crm === filter) : fields;

  return (
    <div className="grid gap-3">
      {err && <div className="text-red-600">{err}</div>}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12px] text-[#3C66AE]">Filtrer par table :</span>
        <button onClick={() => setFilter("")}
          className={`text-[11px] px-2 py-1 rounded ${filter === "" ? "bg-[#172343] text-white" : "bg-[#e5e9f1] text-[#3C66AE]"}`}>Toutes ({fields.length})</button>
        {tables.map((t) => (
          <button key={t} onClick={() => setFilter(t)}
            data-testid={`myo-fields-table-${t}`}
            className={`text-[11px] px-2 py-1 rounded ${filter === t ? "bg-[#172343] text-white" : "bg-[#e5e9f1] text-[#3C66AE]"}`}>{t}</button>
        ))}
      </div>
      <div className="grid gap-2">
        {filtered.map((f) => (
          <FieldCard key={`${f.table_crm}::${f.champ}`} f={f} canAnnotate={canAnnotate} onSave={annotate} />
        ))}
      </div>
    </div>
  );
}

function FieldCard({ f, canAnnotate, onSave }) {
  const [open, setOpen] = useState(false);
  const ann = f.annotation;
  const [souhait, setSouhait] = useState(ann?.souhait || "");
  const [priority, setPriority] = useState(ann?.priority || "moyenne");
  const [comment, setComment] = useState(ann?.comment || "");
  const souhaits = [
    { v: "keep", l: "Conserver tel quel" },
    { v: "highlight", l: "Mettre en avant" },
    { v: "annotate_only", l: "Annoter uniquement" },
    { v: "hide", l: "Masquer pour mon espace" },
    { v: "delete", l: "Supprimer du CRM" },
    { v: "add_similar", l: "Ajouter un champ similaire" },
  ];
  return (
    <div className="bg-[#f9fafc] rounded-lg border border-[#e5e9f1] p-3" data-testid={`myo-field-${f.table_crm}-${f.champ}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#172343]/10 text-[#172343]">{f.table_crm}</span>
            <span className="text-[10px] uppercase tracking-wider text-[#3C66AE] font-mono">{f.champ}</span>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#3C66AE]/10 text-[#3C66AE]">{f.statut_actuel || "—"}</span>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#ED761E]/10 text-[#ED761E]">visible: {f.visible_industriel || "—"}</span>
            {ann && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Annoté ({ann.souhait})</span>}
          </div>
          <div className="text-[12px] text-[#172343]">{f.description_fonctionnelle}</div>
          <div className="text-[11px] text-[#3C66AE]/70">Type : {f.type_donnee}</div>
        </div>
        {canAnnotate && (
          <button onClick={() => setOpen(!open)}
            data-testid={`myo-field-${f.table_crm}-${f.champ}-toggle`}
            className="px-2 py-1 rounded text-[11px] font-medium bg-[#172343] text-white hover:bg-[#0F1A33] inline-flex items-center gap-1 shrink-0">
            {ann ? "Modifier" : "Annoter"} <ChevronDown size={10} className={open ? "rotate-180" : ""} />
          </button>
        )}
      </div>
      {open && canAnnotate && (
        <div className="mt-3 pt-3 border-t border-[#e5e9f1] grid gap-2">
          <select value={souhait} onChange={(e) => setSouhait(e.target.value)}
            data-testid={`myo-field-${f.table_crm}-${f.champ}-souhait`}
            className="px-2 py-1.5 border border-[#e5e9f1] rounded text-[13px]">
            <option value="">Souhait industriel…</option>
            {souhaits.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)}
            className="px-2 py-1.5 border border-[#e5e9f1] rounded text-[13px]">
            <option value="haute">Haute</option><option value="moyenne">Moyenne</option><option value="basse">Basse</option>
          </select>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Commentaire (optionnel)"
            className="px-2 py-1.5 border border-[#e5e9f1] rounded text-[13px] resize-y" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="px-3 py-1 text-[12px] text-[#3C66AE]">Annuler</button>
            <button onClick={() => { if (souhait) { onSave(f.table_crm, f.champ, { souhait, priority, comment }); setOpen(false); } }}
              data-testid={`myo-field-${f.table_crm}-${f.champ}-save`} disabled={!souhait}
              className="px-3 py-1 rounded text-[12px] bg-[#ED761E] hover:bg-[#D86813] text-white disabled:opacity-50">Enregistrer</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Admin ---------------- */
function Admin() {
  const [accounts, setAccounts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState("accounts");
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState("");
  const [newAcc, setNewAcc] = useState({ name: "", contact_email: "" });

  const reload = useCallback(async () => {
    try {
      const [a, l] = await Promise.all([
        axios.get(`${apiBase}/admin/industrial-accounts`),
        axios.get(`${apiBase}/admin/audit-logs`),
      ]);
      setAccounts(a.data); setLogs(l.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true); setImportResult("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${apiBase}/admin/import-pack`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(`✓ Pack importé : ${data.patients} patients · ${data.steps} étapes · ${data.measurements} mesures · ${data.behaviors} comportements · ${data.answers} réponses · ${data.governance_questions} questions · ${data.governance_fields} champs`);
      reload();
    } catch (err) {
      setImportResult(`✗ ${err?.response?.data?.detail || "Échec"}`);
    } finally {
      setImportBusy(false);
      e.target.value = "";
    }
  };

  const createAcc = async () => {
    if (!newAcc.name || !newAcc.contact_email) return;
    try {
      await axios.post(`${apiBase}/admin/industrial-accounts`, newAcc);
      setNewAcc({ name: "", contact_email: "" });
      reload();
    } catch (err) { alert(err?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="myo-admin">
      <h1 className="text-2xl font-semibold text-[#172343] mb-1">Administration MetaConnect</h1>
      <p className="text-[13px] text-[#3C66AE] mb-6">Gestion des espaces industriels, import du pack CRM, journal d'audit.</p>

      <div className="bg-white rounded-xl border border-[#e5e9f1] p-5 mb-4">
        <div className="text-[14px] font-medium text-[#172343] mb-3 inline-flex items-center gap-2">
          <Upload size={16} />Import pack CRM industriel (multi-onglets)
        </div>
        <p className="text-[12px] text-[#3C66AE]/80 mb-3">
          Onglets attendus : <code className="bg-[#f5f7fb] px-1 rounded">Patients_CRM, Suivi_Etapes, Mesures_OD_OG, Comportements, Questionnaires, Gouvernance_Questions, Gouvernance_Champs</code>
        </p>
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#172343] text-white text-[13px] cursor-pointer hover:bg-[#0F1A33]">
          <Upload size={14} />
          {importBusy ? "Import en cours…" : "Choisir un fichier Excel"}
          <input data-testid="myo-import-input" type="file" accept=".xlsx,.xls" className="hidden" onChange={onImport} disabled={importBusy} />
        </label>
        {importResult && <div className="mt-3 text-[12px]" data-testid="myo-import-result">{importResult}</div>}
      </div>

      <div className="bg-white rounded-xl border border-[#e5e9f1]">
        <div className="border-b border-[#e5e9f1] px-5 flex">
          {[{ k: "accounts", l: "Espaces industriels" }, { k: "logs", l: "Journal d'audit" }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`myo-admin-tab-${t.k}`}
              className={`py-3 px-4 text-[13px] border-b-2 transition ${
                tab === t.k ? "border-[#ED761E] text-[#ED761E]" : "border-transparent text-[#3C66AE] hover:text-[#172343]"
              }`}>{t.l}</button>
          ))}
        </div>
        <div className="p-5">
          {tab === "accounts" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                <input placeholder="Nom (ex. Essilor France)" value={newAcc.name}
                  onChange={(e) => setNewAcc({ ...newAcc, name: e.target.value })}
                  className="px-3 py-2 border border-[#e5e9f1] rounded text-[13px]" />
                <input placeholder="Email contact" type="email" value={newAcc.contact_email}
                  onChange={(e) => setNewAcc({ ...newAcc, contact_email: e.target.value })}
                  className="px-3 py-2 border border-[#e5e9f1] rounded text-[13px]" />
                <button onClick={createAcc}
                  data-testid="myo-create-account"
                  className="px-4 py-2 rounded bg-[#ED761E] text-white text-[13px] hover:bg-[#D86813] inline-flex items-center justify-center gap-1">
                  <Plus size={13} />Créer un espace
                </button>
              </div>
              <table className="w-full text-[13px]">
                <thead className="text-[10px] uppercase tracking-wider text-[#3C66AE]">
                  <tr><th className="text-left p-2">Nom</th><th className="text-left p-2">Email contact</th><th className="text-left p-2">Créé le</th></tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.id} className="border-t border-[#e5e9f1]">
                      <td className="p-2 text-[#172343]">{a.name}</td>
                      <td className="p-2 text-[#3C66AE] font-mono">{a.contact_email}</td>
                      <td className="p-2 text-[#3C66AE]/70">{new Date(a.created_at).toLocaleString("fr-FR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {tab === "logs" && (
            <div className="grid gap-1.5 max-h-[600px] overflow-y-auto">
              {logs.length === 0 && <div className="text-[12px] text-[#3C66AE]/60 italic">Aucun événement</div>}
              {logs.map((l, i) => (
                <div key={i} className="grid grid-cols-[160px_1fr_120px] gap-3 text-[12px] py-1.5 border-b border-[#e5e9f1]/60">
                  <span className="text-[#3C66AE] font-mono">{new Date(l.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" })}</span>
                  <span><span className="text-[#172343] font-medium">{l.action}</span> <span className="text-[#3C66AE]/70">— {JSON.stringify(l.meta || {}).slice(0, 80)}</span></span>
                  <span className="text-right text-[#3C66AE]/70 font-mono truncate">{(l.user_id || "—").slice(0, 8)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- MSSanté Boxes (présentation industriel) ---------------- */
function MssanteBoxes() {
  const advantages = [
    "Sécuriser les échanges interprofessionnels (médecin, opticien, orthoptiste, secrétariat…)",
    "Structurer la communication entre établissements et équipes",
    "Améliorer la traçabilité des échanges",
    "Centraliser les communications sensibles dans un espace de confiance",
    "Renforcer la crédibilité et l'identité numérique de votre organisation",
    "Conformité avec les exigences du numérique en santé (Ségur, Mon Espace Santé)",
  ];
  const audiences = [
    "Médecins", "Secrétaires médicales", "Opticiens", "Orthoptistes",
    "Établissements", "Partenaires", "Collaborateurs internes",
  ];
  const examples = [
    "prenom.nom@votredomaine.mssante.fr",
    "secretariat@votredomaine.mssante.fr",
    "coordination@votredomaine.mssante.fr",
  ];
  return (
    <div data-testid="myo-mssante-page" className="space-y-6">
      <div>
        <h1 className="text-[20px] sm:text-2xl font-semibold text-[#172343]">Boîtes MSSanté</h1>
        <p className="text-[13px] text-[#3C66AE] mt-1">Communication sécurisée & domaine MSSanté dédié à votre marque industrielle.</p>
      </div>

      {/* Hero card */}
      <section className="rounded-xl overflow-hidden border border-[#e5e9f1] bg-gradient-to-br from-[#172343] to-[#0F1A33] text-white p-6 sm:p-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
          <div className="flex-1 max-w-3xl min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-2">Infrastructure MSSanté</div>
            <h2 className="text-[22px] sm:text-[28px] font-semibold leading-tight mb-3">
              Réservez votre domaine MSSanté dédié et sécurisez les échanges entre vos équipes, vos partenaires et l'ensemble de votre écosystème santé.
            </h2>
            <p className="text-[14px] text-white/80 mb-5">
              MetaConnect.fr met à disposition des industriels et organisations de santé une infrastructure
              MSSanté complète permettant de déployer un domaine de messagerie sécurisée dédié à leur marque.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="https://metaconnect.fr/mssante/inscription"
                target="_blank" rel="noopener noreferrer"
                data-testid="myo-mssante-cta-primary"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#ED761E] hover:bg-[#D86813] text-white text-[14px] font-medium transition">
                <Mail size={16} />Réserver mon domaine MSSanté
              </a>
              <a href="https://metaconnect.fr/produit"
                target="_blank" rel="noopener noreferrer"
                data-testid="myo-mssante-cta-secondary"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-[14px] font-medium border border-white/20 transition">
                En savoir plus
              </a>
            </div>
          </div>
          <div className="w-full lg:w-72 shrink-0 flex flex-col items-center lg:items-end">
            <div className="bg-white/95 rounded-xl p-4 sm:p-5 w-full max-w-[280px]">
              <img src="/logos/mssante-official.png" alt="MSSanté — Système de messageries sécurisées"
                data-testid="myo-mssante-official-logo"
                className="w-full h-auto object-contain" />
            </div>
            <div className="mt-2 text-[10px] text-white/50 text-center lg:text-right">Label officiel · ANS</div>
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-[#e5e9f1] p-5 sm:p-6">
          <h3 className="text-[15px] font-semibold text-[#172343] mb-3">Bénéfices stratégiques</h3>
          <ul className="space-y-2">
            {advantages.map((a) => (
              <li key={a} className="flex items-start gap-2 text-[13px] text-[#172343]">
                <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-50 text-emerald-700 font-mono text-[11px] shrink-0">✓</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-xl border border-[#e5e9f1] p-5 sm:p-6">
          <h3 className="text-[15px] font-semibold text-[#172343] mb-3">Pour qui ?</h3>
          <p className="text-[13px] text-[#3C66AE] mb-3">Les boîtes nominatives et organisationnelles facilitent les échanges entre :</p>
          <div className="flex flex-wrap gap-2 mb-5">
            {audiences.map((a) => (
              <span key={a} className="text-[12px] px-3 py-1 rounded-full bg-[#f5f7fb] text-[#172343] border border-[#e5e9f1]">{a}</span>
            ))}
          </div>
          <h4 className="text-[12px] font-semibold text-[#172343] mb-2 mt-4">Exemples d'adresses</h4>
          <div className="grid gap-1.5">
            {examples.map((e) => (
              <code key={e} className="text-[12px] font-mono px-3 py-2 rounded bg-[#f5f7fb] text-[#3C66AE] border border-[#e5e9f1]">{e}</code>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-[#e5e9f1] p-5 sm:p-6">
        <h3 className="text-[15px] font-semibold text-[#172343] mb-2">Continuité organisationnelle</h3>
        <p className="text-[13px] text-[#3C66AE] leading-relaxed">
          L'architecture MSSanté permet également de conserver une <strong>continuité organisationnelle</strong> même
          en cas de changement de personnel ou de structure interne. Les boîtes organisationnelles
          (<code className="font-mono text-[12px] bg-[#f5f7fb] px-1.5 py-0.5 rounded">secretariat@…</code>,
          <code className="font-mono text-[12px] bg-[#f5f7fb] px-1.5 py-0.5 rounded ml-1">coordination@…</code>)
          restent rattachées à votre structure et survivent aux mouvements RH, sans perte d'historique
          ni rupture de communication avec vos partenaires.
        </p>
      </section>

      <section className="rounded-xl border border-[#ED761E]/30 bg-[#ED761E]/5 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="text-[14px] font-semibold text-[#172343]">Prêt à déployer votre domaine MSSanté ?</div>
          <div className="text-[12px] text-[#3C66AE] mt-0.5">Démarrage rapide · accompagnement par MetaConnect · conformité Ségur garantie.</div>
        </div>
        <a href="https://metaconnect.fr/mssante/inscription"
          target="_blank" rel="noopener noreferrer"
          data-testid="myo-mssante-cta-bottom"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#ED761E] hover:bg-[#D86813] text-white text-[14px] font-medium transition shrink-0">
          <Mail size={16} />S'inscrire
        </a>
      </section>
    </div>
  );
}

/* ---------------- Team / Collaborateurs management ---------------- */
function fallbackCopy(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  } catch { /* noop */ }
}

function TeamPage({ user }) {
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newCreds, setNewCreds] = useState(null);
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [u, a] = await Promise.all([
        axios.get(`${apiBase}/users`),
        user.role === "super_admin" ? axios.get(`${apiBase}/admin/industrial-accounts`) : Promise.resolve({ data: [] }),
      ]);
      setUsers(u.data); setAccounts(a.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  }, [user.role]);
  useEffect(() => { reload(); }, [reload]);

  const onCreated = (data) => { setNewCreds(data); setShowCreate(false); reload(); };
  const toggleActive = async (u) => {
    try {
      await axios.patch(`${apiBase}/users/${u.id}`, { active: !u.active });
      reload();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  };
  const regenerate = async (u) => {
    if (!window.confirm(`Régénérer le mot de passe de ${u.name} ?`)) return;
    try {
      const { data } = await axios.post(`${apiBase}/users/${u.id}/regenerate-password`);
      setNewCreds({ ...u, provisional_password: data.provisional_password });
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.name} ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${apiBase}/users/${u.id}`);
      reload();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  };
  const copyToClipboard = (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
      } else {
        fallbackCopy(text);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // never crash the UI on copy failure
      console.warn("clipboard copy failed", e);
    }
  };

  // Defensive : if newCreds present but malformed, render only safe fallback
  const credsValid = newCreds && typeof newCreds === "object" && newCreds.provisional_password;

  return (
    <div data-testid="myo-team-page" className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-[20px] sm:text-2xl font-semibold text-[#172343]">Gouvernance des accès & collaborateurs</h1>
          <p className="text-[13px] text-[#3C66AE] mt-1">
            Créez et administrez vos utilisateurs en autonomie. Toutes les actions sont journalisées.
          </p>
        </div>
        <button onClick={() => { setShowCreate(true); setNewCreds(null); }}
          data-testid="myo-team-create-btn"
          className="px-4 py-2 rounded-lg bg-[#ED761E] hover:bg-[#D86813] text-white text-[14px] font-medium inline-flex items-center justify-center gap-2 shrink-0">
          <UserPlus size={15} />Créer un collaborateur
        </button>
      </div>

      {/* Provisional credentials reveal */}
      {credsValid && (
        <section className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-5" data-testid="myo-team-creds-card">
          <div className="flex items-start gap-3 mb-3">
            <Check size={20} className="text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-[14px] font-semibold text-emerald-900">Identifiants générés pour {newCreds.name || "—"}</h3>
              <p className="text-[12px] text-emerald-800 mt-0.5">
                Communiquez ces accès au collaborateur de manière sécurisée. Ce mot de passe ne sera plus affiché.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <CredField label="Identifiant" value={newCreds.username || newCreds.email || ""} onCopy={copyToClipboard} copied={copied} />
            <CredField label="Mot de passe provisoire" value={newCreds.provisional_password || ""} onCopy={copyToClipboard} copied={copied} mono />
          </div>
          <div className="text-[11px] text-emerald-800 mt-3 italic">
            Afin de renforcer la sécurité des accès, ce mot de passe provisoire devra être renouvelé lors de la première connexion.
          </div>
          <button onClick={() => setNewCreds(null)} data-testid="myo-team-creds-close"
            className="mt-3 text-[12px] text-emerald-700 hover:underline">J'ai noté les accès, fermer</button>
        </section>
      )}

      {/* Create form */}
      {showCreate && (
        <CreateUserForm user={user} accounts={accounts} onCreated={onCreated} onCancel={() => setShowCreate(false)} />
      )}

      {err && <div className="text-red-600 text-[13px]">{err}</div>}
      {busy && <div className="text-[#3C66AE] text-[13px]"><Loader2 size={14} className="animate-spin inline mr-1" />Chargement…</div>}

      {/* Mobile cards */}
      <div className="md:hidden grid gap-3">
        {users.map((u) => (
          <UserCard key={u.id} u={u} accounts={accounts} currentUser={user}
            onToggleActive={toggleActive} onRegenerate={regenerate} onDelete={removeUser} />
        ))}
        {!busy && users.length === 0 && <div className="text-center text-[#3C66AE]/60 italic p-12">Aucun collaborateur.</div>}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-[#e5e9f1] overflow-x-auto">
        <table className="w-full text-[13px] min-w-[800px]">
          <thead className="bg-[#f5f7fb] text-[10px] uppercase tracking-wider text-[#3C66AE]">
            <tr>
              <th className="text-left p-3">Nom</th>
              <th className="text-left p-3">Identifiant</th>
              <th className="text-left p-3">Rôle</th>
              <th className="text-left p-3">Statut</th>
              {user.role === "super_admin" && <th className="text-left p-3">Espace</th>}
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const acc = accounts.find((a) => a.id === u.industrial_account_id);
              const isSelf = u.id === user.id;
              // Industrial role users cannot manage super_admin accounts.
              // Hide regen/toggle/delete buttons entirely on these rows.
              const canManage = user.role === "super_admin" || u.role !== "super_admin";
              return (
                <tr key={u.id} className="border-t border-[#e5e9f1]" data-testid={`myo-user-row-${u.id}`}>
                  <td className="p-3">
                    <div className="text-[#172343] font-medium">{u.name}</div>
                    {u.fonction && <div className="text-[11px] text-[#3C66AE]/70">{u.fonction}</div>}
                  </td>
                  <td className="p-3">
                    <div className="font-mono text-[12px] text-[#172343]">{u.username || u.email || "—"}</div>
                    {u.must_change_password && <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded mt-0.5 inline-block">MdP à changer</span>}
                  </td>
                  <td className="p-3"><RoleBadge role={u.role} /></td>
                  <td className="p-3">
                    {u.active ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Actif</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Désactivé</span>
                    )}
                  </td>
                  {user.role === "super_admin" && <td className="p-3 text-[12px] text-[#3C66AE]">{acc?.name || "—"}</td>}
                  <td className="p-3 text-right whitespace-nowrap">
                    <div className="inline-flex gap-1">
                      {!canManage ? (
                        <span
                          className="text-[10px] uppercase tracking-wider text-[#3C66AE]/50 italic px-2"
                          data-testid={`myo-user-locked-${u.id}`}
                          title="Compte administrateur — gestion réservée à MetaConnect"
                        >
                          Réservé
                        </span>
                      ) : (
                        <>
                          <button onClick={() => regenerate(u)} disabled={isSelf}
                            data-testid={`myo-user-regen-${u.id}`}
                            title="Régénérer le mot de passe"
                            className="p-1.5 rounded text-[#3C66AE] hover:bg-[#f5f7fb] disabled:opacity-40 disabled:cursor-not-allowed">
                            <RefreshCw size={14} />
                          </button>
                          <button onClick={() => toggleActive(u)} disabled={isSelf}
                            data-testid={`myo-user-toggle-${u.id}`}
                            title={u.active ? "Désactiver" : "Réactiver"}
                            className="p-1.5 rounded text-[#3C66AE] hover:bg-[#f5f7fb] disabled:opacity-40 disabled:cursor-not-allowed">
                            {u.active ? <X size={14} /> : <Check size={14} />}
                          </button>
                          <button onClick={() => removeUser(u)} disabled={isSelf}
                            data-testid={`myo-user-delete-${u.id}`}
                            title="Supprimer"
                            className="p-1.5 rounded text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!busy && users.length === 0 && (
              <tr><td colSpan={user.role === "super_admin" ? 6 : 5} className="p-12 text-center text-[#3C66AE]/60 italic">Aucun collaborateur.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CredField({ label, value, onCopy, copied, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-emerald-800 mb-1">{label}</div>
      <div className="flex items-center gap-2 bg-white rounded-lg border border-emerald-200 px-3 py-2">
        <code className={`flex-1 text-[13px] text-[#172343] truncate ${mono ? "font-mono" : ""}`}>{value}</code>
        <button onClick={() => onCopy(value)} className="p-1 rounded text-emerald-700 hover:bg-emerald-50">
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const map = {
    super_admin: { l: "Super admin", cls: "bg-[#172343] text-white" },
    industrial: { l: "Industriel", cls: "bg-[#3C66AE]/10 text-[#3C66AE]" },
    observer: { l: "Observateur", cls: "bg-[#f5f7fb] text-[#64748b]" },
  };
  const c = map[role] || { l: role, cls: "bg-[#f5f7fb] text-[#64748b]" };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${c.cls}`}>{c.l}</span>;
}

function UserCard({ u, accounts, currentUser, onToggleActive, onRegenerate, onDelete }) {
  const acc = accounts.find((a) => a.id === u.industrial_account_id);
  const isSelf = u.id === currentUser.id;
  return (
    <div className="bg-white rounded-xl border border-[#e5e9f1] p-4" data-testid={`myo-user-card-${u.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-[14px] text-[#172343] font-medium">{u.name}</div>
          {u.fonction && <div className="text-[11px] text-[#3C66AE]/70">{u.fonction}</div>}
          <div className="font-mono text-[12px] text-[#3C66AE] mt-1 truncate">{u.username || u.email || "—"}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <RoleBadge role={u.role} />
          {u.active ? (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Actif</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Désactivé</span>
          )}
        </div>
      </div>
      {acc && currentUser.role === "super_admin" && <div className="text-[11px] text-[#3C66AE]/70 mb-2">{acc.name}</div>}
      {u.must_change_password && <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded mb-2 inline-block">Mot de passe à changer</span>}
      <div className="flex gap-2 pt-2 border-t border-[#e5e9f1]">
        <button onClick={() => onRegenerate(u)} disabled={isSelf}
          className="text-[11px] text-[#3C66AE] hover:underline disabled:opacity-40 inline-flex items-center gap-1">
          <RefreshCw size={11} />Régénérer MdP
        </button>
        <button onClick={() => onToggleActive(u)} disabled={isSelf}
          className="text-[11px] text-[#3C66AE] hover:underline disabled:opacity-40">
          {u.active ? "Désactiver" : "Activer"}
        </button>
        <button onClick={() => onDelete(u)} disabled={isSelf}
          className="text-[11px] text-red-600 hover:underline disabled:opacity-40 ml-auto">Supprimer</button>
      </div>
    </div>
  );
}

function CreateUserForm({ user, accounts, onCreated, onCancel }) {
  const [form, setForm] = useState({
    name: "", fonction: "", username: "", email: "",
    role: user.role === "super_admin" ? "industrial" : "industrial",
    industrial_account_id: user.role === "industrial" ? user.industrial_account_id : "",
    password: "", autoGen: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        fonction: form.fonction.trim() || null,
        role: form.role,
      };
      if (form.username.trim()) payload.username = form.username.trim();
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.industrial_account_id) payload.industrial_account_id = form.industrial_account_id;
      if (!form.autoGen && form.password) payload.password = form.password;
      const { data } = await axios.post(`${apiBase}/users`, payload);
      onCreated(data);
    } catch (e) { setErr(extractErrorMessage(e, "Création impossible. Vérifiez que l'identifiant n'est pas déjà utilisé.")); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="myo-team-create-form"
      className="bg-white rounded-xl border border-[#e5e9f1] p-5 sm:p-6 space-y-4">
      <h3 className="text-[15px] font-semibold text-[#172343]">Nouveau collaborateur</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Nom complet *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="myo-create-name" />
        <Input label="Fonction / rôle métier" value={form.fonction} onChange={(v) => setForm({ ...form, fonction: v })} testid="myo-create-fonction" placeholder="Data Manager, Médecin…" />
        <Input label="Identifiant (username)" value={form.username} onChange={(v) => setForm({ ...form, username: v })} testid="myo-create-username" placeholder="prenom.nom" />
        <Input label="Email professionnel (optionnel)" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" testid="myo-create-email" />
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#3C66AE]">Niveau d'accès *</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="myo-create-role"
            className="w-full mt-1 px-3 py-2 border border-[#cbd3e1] rounded-lg text-[14px] bg-white text-[#172343]">
            {user.role === "super_admin" && <option value="super_admin">Super admin (MetaConnect)</option>}
            <option value="industrial">Industriel (gestion)</option>
            <option value="observer">Observateur (lecture)</option>
          </select>
        </div>
        {user.role === "super_admin" && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#3C66AE]">Espace industriel</label>
            <select value={form.industrial_account_id} onChange={(e) => setForm({ ...form, industrial_account_id: e.target.value })}
              data-testid="myo-create-account"
              className="w-full mt-1 px-3 py-2 border border-[#cbd3e1] rounded-lg text-[14px] bg-white text-[#172343]">
              <option value="">— Aucun (super admin) —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div>
        <label className="text-[12px] inline-flex items-center gap-2 text-[#172343]">
          <input type="checkbox" checked={form.autoGen} onChange={(e) => setForm({ ...form, autoGen: e.target.checked })}
            data-testid="myo-create-autogen" />
          Générer un mot de passe sécurisé (recommandé)
        </label>
        {!form.autoGen && (
          <div className="mt-2">
            <Input label="Mot de passe provisoire (min 8 car.)" value={form.password} type="password"
              onChange={(v) => setForm({ ...form, password: v })} testid="myo-create-password" />
          </div>
        )}
      </div>
      <div className="bg-[#f5f7fb] border border-[#e5e9f1] rounded-lg p-3 text-[12px] text-[#3C66AE] flex items-start gap-2">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <span>Pour des raisons de sécurité, le collaborateur devra <strong>modifier son mot de passe lors de sa première connexion</strong>.</span>
      </div>
      {err && <div className="text-red-600 text-[13px]" data-testid="myo-create-err">{err}</div>}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-[13px] text-[#3C66AE]">Annuler</button>
        <button type="submit" disabled={busy || !form.name}
          data-testid="myo-create-submit"
          className="px-5 py-2 rounded-lg bg-[#172343] hover:bg-[#0F1A33] text-white text-[14px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />}
          Créer le collaborateur
        </button>
      </div>
    </form>
  );
}

function Input({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#3C66AE]">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        required={required} data-testid={testid} placeholder={placeholder}
        className="w-full mt-1 px-3 py-2 border border-[#cbd3e1] rounded-lg text-[14px] bg-white text-[#172343] placeholder:text-[#94a3b8] focus:outline-none focus:border-[#3C66AE]" />
    </div>
  );
}

/* ---------------- Force password change (1st login) ---------------- */
function ForcePasswordChange({ user, onDone, onLogout }) {
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pwd !== pwd2) { setErr("Les deux mots de passe ne correspondent pas."); return; }
    if (pwd.length < 8) { setErr("Le mot de passe doit contenir au minimum 8 caractères."); return; }
    setBusy(true);
    try {
      await axios.post(`${apiBase}/auth/change-password`, { new_password: pwd });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f5f7fb] to-[#e8eef7] p-4">
      <form onSubmit={submit} data-testid="myo-force-pwd-form"
        className="w-full max-w-md bg-white rounded-xl shadow-xl border border-[#e5e9f1] p-8 space-y-4">
        <div className="flex justify-center mb-2">
          <img src="/logos/myo-control.jpeg" alt="Myo-Control" className="h-16 w-auto object-contain" />
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle size={16} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="text-[12px] text-amber-900">
            <strong>Première connexion détectée</strong><br />
            Pour des raisons de sécurité, vous devez définir un nouveau mot de passe avant d'accéder à votre espace.
          </div>
        </div>
        <div>
          <label className="block text-[12px] font-medium text-[#172343] mb-1.5">Bienvenue {user.name}</label>
          <p className="text-[11px] text-[#64748b]">Compte : <span className="font-mono text-[#172343]">{user.username || user.email}</span></p>
        </div>
        <div>
          <label className="block text-[12px] font-medium text-[#172343] mb-1.5">Nouveau mot de passe (min. 8 caractères)</label>
          <input type="password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)}
            data-testid="myo-force-pwd-1" autoComplete="new-password" autoFocus
            className="w-full px-3 py-2.5 border border-[#cbd3e1] rounded-lg text-[14px] text-[#172343] bg-white" />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-[#172343] mb-1.5">Confirmation</label>
          <input type="password" required minLength={8} value={pwd2} onChange={(e) => setPwd2(e.target.value)}
            data-testid="myo-force-pwd-2" autoComplete="new-password"
            className="w-full px-3 py-2.5 border border-[#cbd3e1] rounded-lg text-[14px] text-[#172343] bg-white" />
        </div>
        {err && <div className="text-red-600 text-[13px]" data-testid="myo-force-pwd-err">{err}</div>}
        <button type="submit" disabled={busy || !pwd || pwd !== pwd2}
          data-testid="myo-force-pwd-submit"
          className="w-full bg-[#172343] hover:bg-[#0F1A33] text-white py-2.5 rounded-lg text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />}Définir mon mot de passe
        </button>
        <button type="button" onClick={onLogout} data-testid="myo-force-pwd-logout"
          className="w-full text-[12px] text-[#3C66AE] hover:underline">Se déconnecter</button>
      </form>
    </div>
  );
}

/* ---------------- Error Boundary (no more white page ever) ---------------- */
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("Myo-Control crashed:", error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb] p-6">
          <div className="max-w-lg bg-white rounded-xl border border-[#e5e9f1] p-8 text-center">
            <AlertCircle size={32} className="text-red-500 mx-auto mb-3" />
            <h2 className="text-[18px] font-semibold text-[#172343] mb-2">Une erreur s'est produite</h2>
            <p className="text-[13px] text-[#3C66AE] mb-5">
              L'application a rencontré un problème inattendu. Vos données sont en sécurité.
            </p>
            <details className="text-[11px] text-[#64748b] mb-4 text-left bg-[#f5f7fb] p-3 rounded">
              <summary className="cursor-pointer">Détails techniques</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">{String(this.state.error?.message || this.state.error)}</pre>
            </details>
            <button onClick={() => { this.setState({ error: null }); window.location.reload(); }}
              data-testid="myo-error-reload"
              className="px-5 py-2 rounded-lg bg-[#172343] text-white text-[13px] font-medium hover:bg-[#0F1A33]">
              Recharger l'application
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ---------------- Root ---------------- */
const MyoStyles = () => (
  <style>{`
    [data-testid="myo-app"] input,
    [data-testid="myo-app"] select,
    [data-testid="myo-app"] textarea,
    [data-testid="myo-login"] input {
      color: #172343;
    }
    [data-testid="myo-app"] input::placeholder,
    [data-testid="myo-app"] textarea::placeholder,
    [data-testid="myo-login"] input::placeholder {
      color: #94a3b8;
    }
    [data-testid="myo-app"] td { color: #172343; }
  `}</style>
);

export default function MyoControlApp() {
  return (
    <ErrorBoundary>
      <MyoControlAppInner />
    </ErrorBoundary>
  );
}

function MyoControlAppInner() {
  const auth = useAuth();
  const [page, setPage] = useState("dashboard");
  const [validating, setValidating] = useState(!!auth.token);

  // Silent token pre-validation on mount: if a stale/expired Myo token sits in
  // localStorage, hit /auth/me once. On 401, logout silently BEFORE Dashboard
  // mounts and triggers a visible 401 on /dashboard/stats. Dramatically reduces
  // console noise after a deploy or token expiry.
  useEffect(() => {
    if (!auth.token) { setValidating(false); return; }
    let alive = true;
    (async () => {
      try {
        await axios.get(`${apiBase}/auth/me`);
      } catch (e) {
        // 401 → useAuth's response interceptor already cleared the token.
        // For any other error (network), keep the token: user is offline,
        // no point in logging them out.
      } finally {
        if (alive) setValidating(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f5f7fb] to-[#e8eef7]">
        <div className="text-[#3C66AE] inline-flex items-center gap-2 text-[14px]">
          <Loader2 size={18} className="animate-spin" /> Connexion à Myo-Control…
        </div>
      </div>
    );
  }

  if (!auth.token || !auth.user) return (<><MyoStyles /><Login onLogin={auth.login} /></>);
  if (auth.user.must_change_password) {
    return (
      <>
        <MyoStyles />
        <ForcePasswordChange user={auth.user}
          onDone={() => {
            const u = { ...auth.user, must_change_password: false };
            localStorage.setItem(USER_KEY, JSON.stringify(u));
            window.location.reload();
          }}
          onLogout={auth.logout} />
      </>
    );
  }
  return (
    <>
      <MyoStyles />
      <Layout user={auth.user} onLogout={auth.logout} page={page} setPage={setPage}>
        {page === "dashboard" && <Dashboard />}
        {page === "patients" && <Patients />}
        {page === "governance" && <Governance user={auth.user} />}
        {page === "mssante" && <MssanteBoxes />}
        {page === "team" && <TeamPage user={auth.user} />}
        {page === "admin" && auth.user.role === "super_admin" && <Admin />}
      </Layout>
    </>
  );
}
